import React from 'react';
import { Modal, Spinner, Badge } from 'reactstrap';
import { motion } from 'framer-motion';
import { X, FileText, AlertTriangle } from 'lucide-react';
import { formatCreatedDate } from '../../constants/utils';
import { WorkOrderStatusBadge } from '../../components/Common';
import { useTranslation } from 'react-i18next';

/**
 * Get urgency badge color
 */
const getUrgencyColor = (urgency) => {
  switch (urgency?.toLowerCase()) {
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'secondary';
  }
};

/**
 * Get modal title based on type
 */
const getModalTitle = (type, t) => {
  switch (type?.toUpperCase()) {
    case 'CREATED':
      return t('reporting.work_order_list.created', 'Created Work Orders');
    case 'RESOLVED':
      return t('shared.resolved_work_orders', 'Resolved Work Orders');
    case 'PREVENTIVE':
      return t('reporting.work_order_list.preventive', 'Preventive Work Orders');
    case 'REACTIVE':
      return t('reporting.work_order_list.reactive', 'Reactive Work Orders');
    case 'OTHER':
      return t('reporting.work_order_list.other', 'Other Work Orders');
    case 'REPEATING':
      return t('reporting.work_order_list.repeating', 'Repeating Work Orders');
    case 'NON_REPEATING':
      return t('reporting.work_order_list.non_repeating', 'Non-Repeating Work Orders');
    case 'COMPLETED_BY_USER':
      return t('reporting.work_order_list.completed', 'Completed Work Orders');
    case 'OVERDUE':
      return t('reporting.work_order_list.overdue', 'Overdue Work Orders');
    default:
      if (type?.includes('ON_TIME')) return t('reporting.work_order_list.on_time', 'On Time Work Orders');
      if (type?.includes('NOT_DONE_OVERDUE')) return t('reporting.work_order_list.not_done_overdue', 'Not Done Overdue Work Orders');
      if (type?.includes('DONE_OVERDUE')) return t('reporting.work_order_list.done_overdue', 'Done Overdue Work Orders');
      return t('shared.work_orders', 'Work Orders');
  }
};

const WorkOrderListModal = ({
  isOpen,
  toggle,
  workOrderList,
  loading,
  error,
  onWorkOrderClick,
  onAssetClick,
  onLocationClick
}) => {
  const { t } = useTranslation();
  const { workOrders = [], total = 0, type = '', periodLabel = '' } = workOrderList || {};

  const title = getModalTitle(type, t);

  const subtitle = periodLabel
    ? `${periodLabel}`
    : t('reporting.work_order_list.subtitle_total', '{{total}} work orders', { total });

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
      >
        {/* Header */}
        <div className="modal-header border-0 pb-0 d-flex align-items-start">
          <div className="flex-grow-1">
            <h5 className="modal-title fw-bold mb-1">{title}</h5>
            <p className="text-muted mb-0 small">{subtitle}</p>
          </div>
          <motion.button
            type="button"
            className="btn btn-light rounded-circle d-flex align-items-center justify-content-center"
            onClick={toggle}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{ width: '36px', height: '36px', padding: 0 }}
          >
            <X size={18} />
          </motion.button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-2">{t('shared.loading_work_orders', 'Loading work orders...')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-5 text-danger">
              <AlertTriangle size={48} className="mb-3" />
              <p>{error}</p>
            </div>
          ) : workOrders.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FileText size={48} className="mb-3" />
              <p>{t('reporting.work_order_list.empty', 'No work orders found for this period.')}</p>
            </div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {/* Show the Organization column only when at least one row carries
                  subOrgName — i.e. the response came from the Global Dashboard's
                  drill-down endpoints. Per-org drill-downs leave subOrgName
                  null so the column stays hidden and the modal renders
                  identically to before this change. */}
              {(() => {
                const showOrgColumn = workOrders.some((wo) => wo.subOrgName);
                return (
              <table className="table table-hover align-middle">
                <thead className="sticky-top bg-white">
                  <tr>
                    <th className="border-0">#</th>
                    <th className="border-0">{t('shared.title', 'Title')}</th>
                    {showOrgColumn && (
                      <th className="border-0">{t('shared.global_org.organization', 'Organization')}</th>
                    )}
                    <th className="border-0">{t('shared.asset', 'Asset')}</th>
                    <th className="border-0">{t('shared.location', 'Location')}</th>
                    <th className="border-0">{t('shared.due_date', 'Due Date')}</th>
                    <th className="border-0">{t('shared.status', 'Status')}</th>
                    <th className="border-0">{t('shared.urgency', 'Urgency')}</th>
                    <th className="border-0">{t('shared.ticket_number', 'Ticket Number')}</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrders.map((wo, index) => {
                    return (
                      <motion.tr
                        key={wo.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <th scope="row">{index + 1}</th>
                        <td>
                          <span
                            className="text-primary cursor-pointer hover-underline text-truncate d-inline-block"
                            onClick={() => onWorkOrderClick?.(wo)}
                            style={{ cursor: 'pointer', maxWidth: '200px' }}
                            title={wo.title || ''}
                          >
                            {wo.title || t('shared.n_a', 'N/A')}
                          </span>
                        </td>
                        {showOrgColumn && (
                          <td className="text-truncate" style={{ maxWidth: '180px' }} title={wo.subOrgName || ''}>
                            {wo.subOrgName || <span className="text-muted">-</span>}
                          </td>
                        )}
                        <td>
                          {wo.assetName ? (
                            <span
                              className="text-primary cursor-pointer hover-underline text-truncate d-inline-block"
                              onClick={() => onAssetClick?.(wo.assetId)}
                              style={{ cursor: 'pointer', maxWidth: '180px' }}
                              title={wo.assetName}
                            >
                              {wo.assetName}
                            </span>
                          ) : (
                            <span className="text-muted">{t('business.reactive.no_asset', 'No Asset')}</span>
                          )}
                        </td>
                        <td>
                          {wo.locationName ? (
                            <span
                              className="text-primary cursor-pointer hover-underline"
                              onClick={() => onLocationClick?.(wo.locationName)}
                              style={{ cursor: 'pointer' }}
                            >
                              {wo.locationName}
                            </span>
                          ) : (
                            <span className="text-muted">{t('shared.n_a', 'N/A')}</span>
                          )}
                        </td>
                        <td className="text-muted small">
                          {wo.dueDate ? formatCreatedDate(wo.dueDate) : <span className="text-muted">-</span>}
                        </td>
                        <td>
                          <WorkOrderStatusBadge status={wo.status} />
                        </td>
                        <td>
                          {wo.urgencyLevel ? (
                            <Badge color={getUrgencyColor(wo.urgencyLevel)} className="text-capitalize">
                              {t(`shared.${wo.urgencyLevel?.toLowerCase()}`, wo.urgencyLevel)}
                            </Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span
                            className="text-primary cursor-pointer hover-underline"
                            onClick={() => onWorkOrderClick?.(wo)}
                            style={{ cursor: 'pointer' }}
                          >
                            {wo.ticketNumber || t('shared.n_a', 'N/A')}
                          </span>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
                );
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer border-0 pt-0">
          <motion.button
            type="button"
            className="btn btn-light"
            onClick={toggle}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {t('shared.close', 'Close')}
          </motion.button>
        </div>
      </motion.div>
    </Modal>
  );
};

export default WorkOrderListModal;
